// ===========================================
// BUSTED! BLACKJACK - LANDING PAGE
// ===========================================
// Marketing landing page for bustedblackjack.com
// ===========================================

import { useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuthStore } from '../stores';
import { APP_VERSION } from '../version';
import bustedLogo from '../assets/busted.png';

export function LandingPage() {
  const navigate = useNavigate();
  const { isAuthenticated, isLoading } = useAuthStore();

  // Redirect authenticated users to home
  useEffect(() => {
    if (isAuthenticated && !isLoading) {
      navigate('/home');
    }
  }, [isAuthenticated, isLoading, navigate]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-gray-400">Loading...</div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-gray-900 via-gray-800 to-gray-900">
      {/* Hero Section */}
      <section className="relative overflow-hidden">
        {/* Background glow effect */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 left-1/2 -translate-x-1/2 w-[800px] h-[800px] bg-casino-gold-500/10 rounded-full blur-3xl" />
          <div className="absolute top-1/3 left-1/3 w-[400px] h-[400px] bg-green-500/5 rounded-full blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 py-8 sm:py-12">
          {/* Logo and Tagline */}
          <div className="text-center mb-10">
            <img
              src={bustedLogo}
              alt="BUSTED! Blackjack"
              className="h-20 sm:h-24 mx-auto -mb-1 drop-shadow-2xl"
            />
            <p className="text-lg sm:text-xl text-gray-300 italic">
              Blackjack and chill.
            </p>
          </div>

          {/* CTA */}
          <div className="text-center mb-8">
            <Link
              to="/login"
              className="inline-block px-12 py-4 bg-gradient-to-r from-casino-gold-500 to-amber-500 text-black text-xl font-bold rounded-xl shadow-lg shadow-casino-gold-500/30 hover:shadow-casino-gold-500/50 hover:scale-105 transition-all duration-200"
            >
              PLAY NOW - It's Free!
            </Link>
            <p className="mt-3 text-gray-500 text-sm">
              Sign in with Google, Facebook, Discord, or X
            </p>
          </div>

          {/* Title */}
          <div className="text-center mb-6">
            <h1 className="text-3xl sm:text-5xl font-bold text-white">
              BUSTED! <span className="text-casino-gold-400">Blackjack</span>
            </h1>
          </div>
        </div>
      </section>

      {/* Feature Cards - Compact Row */}
      <section className="py-12 border-t border-gray-800">
        <div className="max-w-6xl mx-auto px-4">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Multiplayer */}
            <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-6 text-center hover:border-casino-gold-500/50 transition-colors">
              <div className="text-4xl mb-4">🎰</div>
              <h3 className="text-xl font-bold text-white mb-2">Live Multiplayer</h3>
              <p className="text-gray-400">
                Join tables with real players. See their moves in real-time.
                Chat, react, and compete together.
              </p>
            </div>

            {/* Free Chips */}
            <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-6 text-center hover:border-casino-gold-500/50 transition-colors">
              <div className="text-4xl mb-4">💰</div>
              <h3 className="text-xl font-bold text-white mb-2">Free Daily Chips</h3>
              <p className="text-gray-400">
                Claim free B-Bucks every day. Build your bankroll and climb the
                leaderboards without spending a dime.
              </p>
            </div>

            {/* Real Rules */}
            <div className="bg-gray-800/50 backdrop-blur border border-gray-700 rounded-xl p-6 text-center hover:border-casino-gold-500/50 transition-colors">
              <div className="text-4xl mb-4">🃏</div>
              <h3 className="text-xl font-bold text-white mb-2">Casino Rules</h3>
              <p className="text-gray-400">
                Split, double down, insurance, surrender - all the real moves.
                6-deck shoe, dealer stands on soft 17.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Tournament Coming Soon Placeholder */}
      <section className="py-16 px-4">
        <div className="max-w-2xl mx-auto text-center">
          <div className="bg-gradient-to-br from-purple-900/30 to-casino-gold-900/20 border border-casino-gold-500/30 rounded-2xl p-10">
            <div className="text-5xl mb-4">🏆</div>
            <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
              Tournaments Coming Soon
            </h2>
            <p className="text-gray-400 mb-6">
              Compete against players worldwide in daily and weekly tournaments.
              Climb the leaderboards and win exclusive prizes.
            </p>
            <Link
              to="/login"
              className="inline-block px-8 py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-black font-bold rounded-lg transition-colors"
            >
              Sign Up for Updates
            </Link>
          </div>
        </div>
      </section>

      {/* Social Features Section */}
      <section className="py-12">
        <div className="max-w-6xl mx-auto px-4">
          <h2 className="text-2xl sm:text-3xl font-bold text-white text-center mb-8">
            More Than Just a Card Game
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
            {/* Personal Tables */}
            <div className="bg-gradient-to-br from-purple-900/30 to-purple-800/20 border border-purple-500/30 rounded-xl p-5 text-center hover:border-purple-400/50 transition-colors">
              <div className="text-3xl mb-3">🏠</div>
              <h3 className="text-lg font-bold text-white mb-2">Your Own Table</h3>
              <p className="text-gray-400 text-sm">
                Get your own permanent table URL when you sign up. Share it anywhere — your table is always waiting.
              </p>
            </div>

            {/* Invite Friends */}
            <div className="bg-gradient-to-br from-blue-900/30 to-blue-800/20 border border-blue-500/30 rounded-xl p-5 text-center hover:border-blue-400/50 transition-colors">
              <div className="text-3xl mb-3">📨</div>
              <h3 className="text-lg font-bold text-white mb-2">Invite Friends</h3>
              <p className="text-gray-400 text-sm">
                Share a link and play together. One click to join your table.
              </p>
            </div>

            {/* Meet New Friends */}
            <div className="bg-gradient-to-br from-pink-900/30 to-pink-800/20 border border-pink-500/30 rounded-xl p-5 text-center hover:border-pink-400/50 transition-colors">
              <div className="text-3xl mb-3">🤝</div>
              <h3 className="text-lg font-bold text-white mb-2">Meet New Friends</h3>
              <p className="text-gray-400 text-sm">
                Join public tables and meet players from around the world. Chat, react, connect.
              </p>
            </div>

            {/* Voice Chat */}
            <div className="relative bg-gradient-to-br from-cyan-900/30 to-cyan-800/20 border border-cyan-500/30 rounded-xl p-5 text-center hover:border-cyan-400/50 transition-colors overflow-hidden">
              {/* Coming Soon Banner */}
              <div className="absolute top-6 -right-[39px] bg-red-600 text-white text-xs font-bold py-1 px-8 rotate-45 shadow-lg">
                COMING SOON!
              </div>
              <div className="text-3xl mb-3">🎙️</div>
              <h3 className="text-lg font-bold text-white mb-2">Voice Chat</h3>
              <p className="text-gray-400 text-sm">
                Talk to other players at your table. Just like being in a real casino.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* BUSTED! Buddy Section */}
      <section className="relative py-12 bg-gradient-to-r from-green-900/20 via-emerald-900/30 to-green-900/20 border-y border-green-500/20">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <div className="inline-block px-4 py-1 bg-green-500/20 text-green-400 text-sm font-semibold rounded-full mb-4">
            NEW FEATURE
          </div>
          <h2 className="text-3xl sm:text-4xl font-bold text-white mb-4">
            Meet <span className="text-green-400">BUSTED! Buddy</span>
          </h2>
          <p className="text-xl text-gray-300 mb-6">
            Your Personal Blackjack Coach
          </p>
          <p className="text-gray-400 max-w-2xl mx-auto mb-8">
            Never wonder "should I hit or stand?" again. BUSTED! Buddy analyzes every hand
            in real-time and shows you the mathematically optimal play based on perfect
            basic strategy. Learn while you play and watch your win rate climb.
          </p>

          {/* Buddy Features */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 max-w-4xl mx-auto">
            <div className="bg-gray-800/50 rounded-lg p-4 border border-green-500/30">
              <div className="text-2xl mb-2">📊</div>
              <h4 className="font-semibold text-white">Real-Time Advice</h4>
              <p className="text-sm text-gray-400">See the optimal play for every hand</p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-green-500/30">
              <div className="text-2xl mb-2">🧠</div>
              <h4 className="font-semibold text-white">Learn the Odds</h4>
              <p className="text-sm text-gray-400">Understand WHY each play is optimal</p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-green-500/30">
              <div className="text-2xl mb-2">⚡</div>
              <h4 className="font-semibold text-white">Instant Guidance</h4>
              <p className="text-sm text-gray-400">Split-second advice, never slow down the table</p>
            </div>
            <div className="bg-gray-800/50 rounded-lg p-4 border border-green-500/30">
              <div className="text-2xl mb-2">📈</div>
              <h4 className="font-semibold text-white">Track Progress</h4>
              <p className="text-sm text-gray-400">See your stats improve over time</p>
            </div>
          </div>
        </div>
      </section>

      {/* Social Proof / Stats */}
      <section className="py-12">
        <div className="max-w-4xl mx-auto px-4">
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-8 text-center">
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-casino-gold-400">100%</div>
              <div className="text-gray-500 text-sm">Free to Play</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-casino-gold-400">24/7</div>
              <div className="text-gray-500 text-sm">Always Open</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-casino-gold-400">6</div>
              <div className="text-gray-500 text-sm">Deck Shoe</div>
            </div>
            <div>
              <div className="text-3xl sm:text-4xl font-bold text-casino-gold-400">3:2</div>
              <div className="text-gray-500 text-sm">Blackjack Pays</div>
            </div>
          </div>
        </div>
      </section>

      {/* Second CTA */}
      <section className="py-12 text-center">
        <h2 className="text-2xl sm:text-3xl font-bold text-white mb-4">
          Ready to Test Your Skills?
        </h2>
        <p className="text-gray-400 mb-8 max-w-md mx-auto">
          Join thousands of players at the virtual felt. Your seat is waiting.
        </p>
        <Link
          to="/login"
          className="inline-block px-10 py-3 bg-casino-gold-500 hover:bg-casino-gold-400 text-black font-bold rounded-lg transition-colors"
        >
          PLAY NOW
        </Link>
      </section>

      {/* Footer */}
      <footer className="border-t border-gray-800 py-8">
        <div className="max-w-6xl mx-auto px-4">
          <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
            <div className="flex items-center gap-2">
              <img src={bustedLogo} alt="BUSTED!" className="h-8" />
              <span className="text-gray-500 text-sm">
                &copy; {new Date().getFullYear()} BUSTED! Blackjack
              </span>
              <span className="text-gray-600 text-xs ml-2">v{APP_VERSION}</span>
            </div>

            <div className="flex items-center gap-6 text-sm">
              <a href="/terms.html" className="text-gray-500 hover:text-gray-300 transition-colors">
                Terms of Service
              </a>
              <a href="/privacy.html" className="text-gray-500 hover:text-gray-300 transition-colors">
                Privacy Policy
              </a>
            </div>
          </div>

          {/* Legal Notices */}
          <div className="mt-6 pt-6 border-t border-gray-800 text-center">
            <p className="text-gray-600 text-xs max-w-2xl mx-auto">
              <span className="inline-block px-2 py-1 bg-red-500/10 text-red-400 rounded mr-2 font-semibold">18+</span>
              BUSTED! Blackjack is a free-to-play social casino game. No real money gambling.
              B-Bucks have no real-world value and cannot be redeemed for cash.
              Intended for entertainment purposes only.
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
}

export default LandingPage;
